/**
 * EXP-2.3 / EXP-2.5 · The example draft, as pure functions.
 *
 * Captions, verdicts and order live on the item draft and are written by
 * **Save item**. Back does not save and does not discard; Cancel and closing
 * the panel discard. All of that is decided here rather than in the component,
 * so the discard path — the one nobody exercises by hand — can be tested.
 *
 * ---------------------------------------------------------------------------
 * ⚠️ FILES ARE NOT TEXT, AND THAT IS THE WHOLE PROBLEM
 * ---------------------------------------------------------------------------
 * A caption can wait for Save because it is a string in memory. A file cannot,
 * because uploading is what makes a thumbnail appear: an admin who adds four
 * photos and hits Cancel has already put four objects in storage.
 *
 * EXP-2.5 chose the PENDING PATH over a scheduled sweep. Uploads land under
 * `{tenant}/pending/{draftId}/…` and are moved to their final key on save.
 * That choice got firmer once `storage.protect_delete()` was measured: a sweep
 * written in SQL cannot delete anything at all, so the alternative needed the
 * Storage API and a service-role key on a timer. A move is an UPDATE, which
 * needs no delete privilege on the happy path.
 *
 * So every draft entry knows whether its file is still pending, and
 * `pendingPathsToDiscard` is what Cancel hands to the service.
 */

export type ExampleVerdict = 'good' | 'bad';

export interface ExampleDraftEntry {
  /** Stable within the draft. Server ids are reused; new ones are generated. */
  key: string;
  /** Present once saved; absent for an example added in this session. */
  id: string | null;
  /** Object key in the `checklist-examples` bucket, pending or final. */
  storagePath: string;
  /** Signed URL for display. */
  url: string;
  caption: string;
  verdict: ExampleVerdict;
  /** True while the file still sits under the pending prefix. */
  pending: boolean;
}

export interface ExampleDraft {
  /** Scopes the pending prefix for this editing session. */
  draftId: string;
  entries: ExampleDraftEntry[];
  /** Index into `entries`, or null when there is nothing to edit. */
  selected: number | null;
  /** Storage paths of examples removed in this session, for cleanup on save. */
  removedPaths: string[];
}

export function emptyDraft(draftId: string): ExampleDraft {
  return { draftId, entries: [], selected: null, removedPaths: [] };
}

/** Build a draft from what the server already has. Nothing is pending. */
export function draftFromSaved(
  draftId: string,
  saved: Array<{ id: string; storage_path: string; file_url: string; caption: string | null; is_good: boolean }>,
): ExampleDraft {
  return {
    draftId,
    entries: saved.map((s) => ({
      key: s.id,
      id: s.id,
      storagePath: s.storage_path,
      url: s.file_url,
      caption: s.caption ?? '',
      verdict: s.is_good ? 'good' : 'bad',
      pending: false,
    })),
    selected: saved.length > 0 ? 0 : null,
    removedPaths: [],
  };
}

/**
 * Add an uploaded file.
 *
 * ⚠️ THE NEW ENTRY LANDS SELECTED, because EXP-2.3 requires the caption to be
 * focused straight away. An upload that does not select itself makes the admin
 * hunt for the thumbnail they just added, and the caption — the thing that
 * makes the example worth having — is the step most easily skipped.
 *
 * ⚠️ It defaults to 'good'. Most examples are what to do, and a verdict that
 * defaults to "Not this" would label a correct shot as a mistake if the admin
 * never touched the control.
 */
export function addExample(
  draft: ExampleDraft,
  file: { key: string; storagePath: string; url: string },
): ExampleDraft {
  const entry: ExampleDraftEntry = {
    key: file.key,
    id: null,
    storagePath: file.storagePath,
    url: file.url,
    caption: '',
    verdict: 'good',
    pending: true,
  };

  return {
    ...draft,
    entries: [...draft.entries, entry],
    selected: draft.entries.length,
  };
}

export function setCaption(draft: ExampleDraft, index: number, caption: string): ExampleDraft {
  if (!draft.entries[index]) return draft;
  const entries = draft.entries.slice();
  entries[index] = { ...entries[index], caption };
  return { ...draft, entries };
}

export function setVerdict(draft: ExampleDraft, index: number, verdict: ExampleVerdict): ExampleDraft {
  if (!draft.entries[index]) return draft;
  const entries = draft.entries.slice();
  entries[index] = { ...entries[index], verdict };
  return { ...draft, entries };
}

export function select(draft: ExampleDraft, index: number): ExampleDraft {
  if (!draft.entries[index]) return draft;
  return { ...draft, selected: index };
}

/**
 * Move one example up or down.
 *
 * ⚠️ SELECTION FOLLOWS THE PHOTO, NOT THE POSITION. Moving the selected entry
 * and leaving the selection on the old index would silently switch which photo
 * the editor pane is editing, so the next caption keystroke would land on a
 * different example.
 */
export function move(draft: ExampleDraft, index: number, delta: number): ExampleDraft {
  const target = index + delta;
  if (!draft.entries[index] || !draft.entries[target]) return draft;

  const entries = draft.entries.slice();
  const tmp = entries[index];
  entries[index] = entries[target];
  entries[target] = tmp;

  return {
    ...draft,
    entries,
    selected: draft.selected === index ? target : draft.selected === target ? index : draft.selected,
  };
}

/**
 * Remove one example.
 *
 * ⚠️ ITS PATH IS REMEMBERED. A saved example's file must be deleted when the
 * item is saved, not before: Back does not save, so an admin who removes one
 * and then backs out and cancels must get it back. A pending file, which no
 * row points at yet, is remembered the same way and cleaned up by whichever of
 * save or discard happens first.
 */
export function remove(draft: ExampleDraft, index: number): ExampleDraft {
  const victim = draft.entries[index];
  if (!victim) return draft;

  const entries = draft.entries.filter((_, n) => n !== index);

  let selected: number | null = draft.selected;
  if (entries.length === 0) selected = null;
  else if (selected !== null && selected >= index) selected = Math.max(0, selected - 1);

  return {
    ...draft,
    entries,
    selected,
    removedPaths: [...draft.removedPaths, victim.storagePath],
  };
}

/**
 * Everything Cancel must clean out of storage.
 *
 * ⚠️ PENDING FILES ONLY. A saved example's file belongs to the item as it
 * stands on the server, and Cancel means "leave the item as it was". Deleting
 * those here would make Cancel destructive, which is the opposite of what the
 * word promises — and it is the exact bug a test of the discard path exists to
 * catch, because nobody exercises Cancel by hand.
 */
export function pendingPathsToDiscard(draft: ExampleDraft): string[] {
  const live = draft.entries.filter((e) => e.pending).map((e) => e.storagePath);
  const removedPending = draft.removedPaths.filter((p) => isPendingPath(p));
  return [...live, ...removedPending];
}

/** Files to delete once the item is saved: removals that were already saved. */
export function pathsToDeleteOnSave(draft: ExampleDraft): string[] {
  return draft.removedPaths.filter((p) => !isPendingPath(p));
}

/** Pending keys live under `{tenant}/pending/{draftId}/…`. */
export function isPendingPath(path: string): boolean {
  return /(^|\/)pending\//.test(path);
}

/** Where an upload lands before the item is saved. */
export function pendingPath(tenantId: string, draftId: string, fileName: string): string {
  return `${tenantId}/pending/${draftId}/${fileName}`;
}

/** Where it moves to once the item is saved. */
export function finalPath(
  tenantId: string,
  owner: { checklistItemId: number } | { libraryTemplateId: string; libraryItemId: string },
  fileName: string,
): string {
  if ('checklistItemId' in owner) {
    return `${tenantId}/item/${owner.checklistItemId}/${fileName}`;
  }
  return `${tenantId}/library/${owner.libraryTemplateId}/${owner.libraryItemId}/${fileName}`;
}

/**
 * What Save item writes.
 *
 * ⚠️ `sort_order` is assigned from the array's position rather than preserved
 * from the server. The rail is the order, and EXP-2.3 requires the order the
 * viewer shows to be the order the admin arranged. Carrying a stale
 * `sort_order` through a reorder would leave the two disagreeing.
 *
 * ⚠️ A caption of only whitespace becomes NULL, not "". The column's CHECK
 * refuses a blank string, and the viewer distinguishes "nobody wrote a reason"
 * from an empty line.
 */
export interface ExampleWriteRow {
  id: string | null;
  storagePath: string;
  caption: string | null;
  isGood: boolean;
  sortOrder: number;
  wasPending: boolean;
}

export function rowsToWrite(draft: ExampleDraft): ExampleWriteRow[] {
  return draft.entries.map((entry, index) => ({
    id: entry.id,
    storagePath: entry.storagePath,
    caption: entry.caption.trim().length > 0 ? entry.caption.trim() : null,
    isGood: entry.verdict === 'good',
    sortOrder: index,
    wasPending: entry.pending,
  }));
}
